<template>
  <div
    v-if="html"
    class="review-card-reply d-flex align-start rounded-20"
    :class="isMobile ? 'pa-4' : 'review-card-reply--desktop pa-6'"
  >
    <LpuLogoPicture
      v-if="showLpuLogoPicture"
      img-class="review-card-reply__img flex-shrink-0 review-card-reply__img--contain"
      contain
      width="40"
      height="40"
      alt=""
      loading="lazy"
      :files="lpuLogoFiles"
    />
    <img
      v-else
      :src="avatarUrl"
      class="review-card-reply__img flex-shrink-0"
      :class="{ 'review-card-reply__img--contain': !isDoctorReply }"
      width="40"
      height="40"
      alt=""
      loading="lazy"
    >
    <div class="review-card-reply__body d-flex flex-column ga-3 ml-4">
      <div>
        <div class="text-subtitle-2 text--text">
          {{ title }}
        </div>
        <div
          v-if="datetime"
          class="text-body-2 text-info--text"
        >
          {{ datetime }}
        </div>
      </div>
      <div class="review-card-reply__text text-body-1 text--text">
        <div
          :class="{ 'review-card-reply__text--truncated': isTruncated && !isExpanded }"
          v-html="html/* eslint-disable-line vue/no-v-html */"
        />
        <button
          v-if="isTruncated && !isExpanded"
          type="button"
          class="text-body-1 primary--text text-decoration-underline button-reset"
          @click.stop="isExpanded = true"
        >
          {{ tStatic('Раскрыть') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { Rate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/types'
import { formatReplyDate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/formatReviewDates'
import {
  getDoctorAvatarUrl,
  getLpuAvatarUrl,
  resolveFilePath,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getMediaUrl'
import { getLpuLogoImageFiles } from 'src/utils/getLpuAvatarUrl'
import LpuLogoPicture from 'src/components/common/LpuLogoPicture/LpuLogoPicture.vue'
import {
  getReplyHtml,
  isReplyTruncated,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getReplyHtml'

const props = defineProps<{
  rate: Rate
  type: 'lpu' | 'moderator'
}>()

const isMobile = useIsMobile()

const { context }: CommonContextStore = useContextStore()
const isExpanded = ref(false)
const languageIso = computed(() => context.current_language.iso)
const isDoctorReply = computed(() => props.type === 'moderator' && Boolean(props.rate.doctor))
const shouldStripContacts = computed(() => {
  if (props.type === 'lpu') {
    return Boolean(props.rate.lpu_replied?.lputype?.is_private)
  }

  return Boolean(props.rate.lpu?.lputype?.is_private || props.rate.doctor)
})
const html = computed(() => getReplyHtml(
  props.rate,
  props.type === 'lpu' ? 'lpu_reply' : 'moderator',
  shouldStripContacts.value,
  context,
))
const isTruncated = computed(() => isReplyTruncated(html.value))
const title = computed(() => {
  if (props.type === 'lpu') {
    return tDynamic(props.rate.lpu_replied, 'name')
  }

  if (props.rate.doctor) {
    return tDynamic(props.rate.doctor, 'fio_short')
  }

  return tDynamic(props.rate.lpu, 'name')
})
const datetime = computed(() => {
  if (props.type === 'lpu') {
    return formatReplyDate(props.rate.dt_lpu_reply, languageIso.value)
  }

  return formatReplyDate(
    props.rate.doctor ? props.rate.dt_doctor_reply : props.rate.dt_lpu_reply,
    languageIso.value,
  )
})
const avatarUrl = computed(() => {
  if (isDoctorReply.value) {
    return getDoctorAvatarUrl(props.rate.doctor, context)
  }

  if (props.type === 'lpu') {
    return getLpuAvatarUrl(props.rate.lpu_replied, context)
  }

  return getLpuAvatarUrl(props.rate.lpu, context)
})
const replyLpu = computed(() => (props.type === 'lpu' ? props.rate.lpu_replied : props.rate.lpu))
const ff_update_lpu_logo = computed(() => Boolean(context.request.feature_flags.ff_update_lpu_logo))
const showLpuLogoPicture = computed(() => (
  ff_update_lpu_logo.value
  && !isDoctorReply.value
  && !resolveFilePath(replyLpu.value?.avatar_reply)
))
const lpuLogoFiles = computed(() => getLpuLogoImageFiles(replyLpu.value))
</script>

<style scoped lang="scss">
@import '~www/themes/doctors/common/variables';

.review-card-reply {
  background-color: $ui-kit-bg-gray-40;

  &--desktop {
    max-width: 554px;
  }

  &__img {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: $border-radius-md;

    &--contain {
      object-fit: contain;
    }
  }

  :deep(.review-card-reply__img) {
    width: 40px;
    height: 40px;
    object-fit: contain;
    border-radius: $border-radius-md;
  }

  &__body {
    flex: 1 1 0;
    min-width: 0;
  }

  &__text {
    word-break: break-word;
  }

  &__text--truncated {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 13;
  }
}
</style>
